import { Translation, Translator } from '@morningtrain/react-translations'
import {
  ExecuteOperationForModelOnPipe,
  ExecuteOperationOnPipe,
  ModelWithForm,
  SubmitFormOnPipe,
  TriggerPipelineOnFormSubmit,
  WhenLoaded,
  WhenModel,
  WhenModelValue
} from '@morningtrain/react-resources'
import Smartview from 'widgets/smartviews/Smartview'
import { Fieldset } from 'layouts/index'
import * as Fields from 'support/fields'
import * as Displays from 'support/displays'
import SmartviewContentSeparator from 'widgets/smartviews/partials/SmartviewContentSeparator'
import ButtonWrap from 'ui/layouts/ButtonWrap'
import DeleteModelOnClick from 'support/actions/generic/DeleteModelOnClick'
import { SubmitButton } from 'ui/buttons'
import { Pipeline, TriggerPipelineOnClick } from '@morningtrain/react-pipelines'
import React from 'react'
import DeleteButton from 'ui/buttons/DeleteButton'
import { GoToPreviousSmartviewOnPipe, MountSmartviewOnPipe } from '@morningtrain/react-smartviews'
import { Button, SecondaryHeading, WithSubtitle } from 'ui'
import { Can, CanUseModel, useAuth } from '@morningtrain/react-auth'
import Permissions from 'services/Permissions'
import useDepartmentId from 'support/hooks/use-department-id'
import {Env} from "@morningtrain/helpers";

export function EditHistoricShiftsSmartview ({
  create = false,
  id = null,
  size = 'xl',
  refreshPipes = null,
  date = null,
  type = 'normal',
  location = null
}) {
  /// TODO - Make the user ID check stateable with "useUserId"
  const auth = useAuth()
  const [departmentId] = useDepartmentId()

  const mainRefreshPipes = (
    <>
      <ExecuteOperationOnPipe resourceName='shifts.historic' operationName='index' />
      <ExecuteOperationOnPipe resourceName='shifts.historic' operationName='index_with_summary' />
      <ExecuteOperationOnPipe resourceName='shifts.historic' operationName='read' />
    </>
  )

  const HasAutoCheckout = ({ children }) => (
    <Can permission={Permissions.SHIFTS_MANAGE_AUTO_CHECKOUTS}>
      <WhenModelValue name='auto' condition={1}>
        <WhenModelValue name='status' condition='pending'>
          {children}
        </WhenModelValue>
      </WhenModelValue>
    </Can>
  )

  const HasChangeRequest = ({ children }) => (
    <Can permission={Permissions.SHIFTS_MANAGE_CHANGE_REQUESTS}>
      <WhenModelValue name='auto' condition={1} negate>
        <WhenModelValue name='user_id' condition={auth.id} negate>
          <WhenModelValue name='status' condition='pending'>
            {children}
          </WhenModelValue>
        </WhenModelValue>
      </WhenModelValue>
    </Can>
  )

  const footer = (

    <ButtonWrap>
      <CanUseModel permission='api.shifts.historic.approve'>
        <Pipeline>
          <TriggerPipelineOnClick>
            <Button>
              <Translation name='shifts.entries.actions.approve' />
            </Button>
          </TriggerPipelineOnClick>
          <SubmitFormOnPipe />
          <ExecuteOperationForModelOnPipe resourceName='shifts.historic' operationName='approve' />
          {mainRefreshPipes}
          {refreshPipes}
        </Pipeline>
      </CanUseModel>
      <CanUseModel permission='api.shifts.historic.decline'>
        <Pipeline>
          <TriggerPipelineOnClick>
            <Button color='red'>
              <Translation name='shifts.entries.actions.decline' />
            </Button>
          </TriggerPipelineOnClick>
          <SubmitFormOnPipe />
          <ExecuteOperationForModelOnPipe resourceName='shifts.historic' operationName='decline' />
          {mainRefreshPipes}
          {refreshPipes}
        </Pipeline>
      </CanUseModel>
      {create ? null : (
        <DeleteModelOnClick
          resourceName='shifts.historic'
          modelKey='user.name'
          resetSmartview={false}
          pipes={(
            <>
              <Pipeline nested async>
                {mainRefreshPipes}
                {refreshPipes}
              </Pipeline>
              <GoToPreviousSmartviewOnPipe />
            </>
          )}
        >
          <DeleteButton big />
        </DeleteModelOnClick>
      )}
      {create
        ? (
          <SubmitButton>
            <Translation name='generic.save' />
          </SubmitButton>
          )
        : (
          <>
            <WhenModelValue name='status' condition={s => s !== 'pending'}>
              <CanUseModel permission='api.shifts.historic.store'>
                <SubmitButton>
                  <HasAutoCheckout>
                    <Translation name='generic.approve' />
                  </HasAutoCheckout>
                  <WhenModelValue name='status' condition={s => s !== 'pending'}>
                    <Translation name='generic.save' />
                  </WhenModelValue>
                  <WhenModelValue name='status' condition={s => s !== 'pending'} negate>
                    <WhenModelValue name='user_id'>
                      <Translation name='generic.save' />
                    </WhenModelValue>
                  </WhenModelValue>
                </SubmitButton>
              </CanUseModel>
            </WhenModelValue>

            <WhenModelValue name='status' condition={s => s === 'pending'}>
              <WhenModelValue name='user_id' condition={auth.id}>
                <CanUseModel permission='api.shifts.historic.store'>
                  <SubmitButton>
                    <HasAutoCheckout>
                      <Translation name='generic.approve' />
                    </HasAutoCheckout>
                    <WhenModelValue name='status' condition={s => s !== 'pending'}>
                      <Translation name='generic.save' />
                    </WhenModelValue>
                    <WhenModelValue name='status' condition={s => s !== 'pending'} negate>
                      <WhenModelValue name='user_id' condition={auth.id}>
                        <Translation name='generic.save' />
                      </WhenModelValue>
                    </WhenModelValue>
                  </SubmitButton>
                </CanUseModel>
              </WhenModelValue>
            </WhenModelValue>
          </>
        )
      }
    </ButtonWrap>
  )

  const header = (
    <WithSubtitle subtitle={(
      <>
        <Can permission={Permissions.SHIFTS_MANAGE_CHANGE_REQUESTS}>
          <WhenModelValue name='auto' condition={1} negate>
            <WhenModelValue name='updated_by' condition={auth.id} negate>
              <WhenModelValue name='status' condition='pending'>
                <Translation name='shifts.entries.has_change_request' />
              </WhenModelValue>
            </WhenModelValue>
          </WhenModelValue>
        </Can>
        <HasAutoCheckout>
          <Translation name='shifts.entries.is_auto_checked_out' />
        </HasAutoCheckout>
      </>
    )}
    >
      <Translation name={create ? 'create.title' : 'edit.title'} />
    </WithSubtitle>
  )

  const hasWaiter = Env.get('globals.has_waiter');
  const hasKitchen = Env.get('globals.has_kitchen');
  const finalEnum = hasWaiter && hasKitchen ? 'employment_type_category_without_all' : (
    hasWaiter ? 'employment_type_waiter' : 'employment_type_kitchen'
  );

  return (
    <Translator namespace='historicshifts.smartview'>
      <ModelWithForm resourceName='shifts.historic' resetOnSuccess={create} id={id} clean preventParentOperationRefresh={create}>
        {refreshPipes}
        {create === false ? null : (
          <MountSmartviewOnPipe
            smartview={payload => <EditHistoricShiftsSmartview id={payload.response.model.id} />}
            replace={false}
          />
        )}
        <ExecuteOperationOnPipe operationName='index' />

        <Smartview header={header} size={size} footer={footer}>
          <Translator namespace='historicshifts.smartview.fields'>

            <Fieldset cols={1}>
              <WhenLoaded>
                <WhenModel>
                  <div className='form-element'>
                    <label>
                      <span><Translation name='fields.user_id.label' /></span>
                    </label>
                    <Displays.Text name='user.name' />
                  </div>
                </WhenModel>
                <Fields.Hidden name='user_id' />
              </WhenLoaded>
              <WhenModel exists={false}>
                <Fields.ResourceSelector
                    name='user_id'
                    optionsKey='name'
                    resourceName='users.users'
                    operationName='select'
                    label={<Translation name='user_id.label' />}
                />
              </WhenModel>

              <Fields.ResourceSelector
                  resourceName='departments'
                  operationName='select'
                  name='department_id'
                  optionsKey='name'
                  label={<Translation name='department_id.label' />}
                  defaultValue={departmentId}
              />
            </Fieldset>

            <SmartviewContentSeparator />

            <HasChangeRequest>

              <Fieldset cols={1}>
                <Fieldset cols={2}>
                  <Fields.Date name='pending_started_at_date' saveFormat='YYYY-MM-DD' label={<Translation name='pending_started_at_date.label' />} />
                  <Fields.Time name='pending_started_at_time' label={<Translation name='pending_started_at_time.label' />} forceUTC />
                </Fieldset>

                <Fieldset cols={2}>
                  <Fields.Date name='pending_stopped_at_date' saveFormat='YYYY-MM-DD' defaultValue={date} label={<Translation name='pending_stopped_at_date.label' />} />
                  <Fields.Time name='pending_stopped_at_time' label={<Translation name='pending_stopped_at_time.label' />} forceUTC />
                </Fieldset>

                <Fields.Input
                    name='pending_breaks_duration'
                    label={<Translation name='pending_breaks_duration.label' />}
                    defaultValue={0}
                />
              </Fieldset>

            </HasChangeRequest>

            <Fieldset cols={1}>
              {/*<WhenModelValue name='status' condition={s => s !== 'pending'} negate>*/}
              {/*  <Fieldset>*/}
              {/*    <Fieldset cols={2}>*/}
              {/*      <Fields.Date name='pending_started_at_date' saveFormat='YYYY-MM-DD' />*/}
              {/*      <Fields.Time name='pending_started_at_time' fromName='pending_started_at' forceUTC />*/}
              {/*    </Fieldset>*/}

              {/*    <Fieldset cols={2}>*/}
              {/*      <Fields.Date name='pending_stopped_at_date' saveFormat='YYYY-MM-DD' />*/}
              {/*      <Fields.Time name='pending_stopped_at_time' fromName='pending_stopped_at' forceUTC />*/}
              {/*    </Fieldset>*/}

              {/*    <Fields.Input*/}
              {/*        name='pending_breaks_duration'*/}
              {/*        label={<Translation name='pending_breaks_duration.label' />}*/}
              {/*        defaultValue={0}*/}
              {/*    />*/}
              {/*  </Fieldset>*/}
              {/*</WhenModelValue>*/}
              <WhenModelValue name='status' condition={s => s !== 'pending'}>
                <WhenModelValue name='user_id' condition={auth.id}>
                  <Fieldset cols={2}>
                    <Fields.Date name='started_at_date' saveFormat='YYYY-MM-DD' defaultValue={date} />
                    <Fields.Time name='started_at_time' fromName='started_at' forceUTC />
                  </Fieldset>

                  <Fieldset cols={2}>
                    <Fields.Date name='stopped_at_date' saveFormat='YYYY-MM-DD' defaultValue={date} />
                    <Fields.Time name='stopped_at_time' fromName='stopped_at' forceUTC />
                  </Fieldset>

                  <Fields.Input
                      name='breaks_duration'
                      label={<Translation name='breaks_duration.label' />}
                      defaultValue={0}
                  />
                </WhenModelValue>
              </WhenModelValue>

              <WhenModelValue name='status' condition={s => s !== 'pending'}>
                <WhenModelValue name='user_id' condition={auth.id} negate>
                  <Fieldset cols={2}>
                    <Fields.Date name='started_at_date' saveFormat='YYYY-MM-DD' />
                    <Fields.Time name='started_at_time' fromName='started_at' forceUTC />
                  </Fieldset>

                  <Fieldset cols={2}>
                    <Fields.Date name='stopped_at_date' saveFormat='YYYY-MM-DD' defaultValue={date} />
                    <Fields.Time name='stopped_at_time' fromName='stopped_at' forceUTC />
                  </Fieldset>

                  <Fields.Input
                      name='breaks_duration'
                      label={<Translation name='breaks_duration.label' />}
                      defaultValue={0}
                  />
                </WhenModelValue>
              </WhenModelValue>
            </Fieldset>

            <SmartviewContentSeparator />

            <Fieldset cols={2}>
              <Fields.TabsField
                name='location'
                enum={finalEnum}
                label={<Translation name='location.label' />}
                defaultValue={(hasWaiter && hasKitchen) ? location : (hasWaiter ? 'waiter' : 'kitchen')}
              />
              <Fields.TabsField
                name='type'
                enum='toggle_type'
                label={<Translation name='type.label' />}
                only={['1', '2']}
                defaultValue={type === 'admin' ? '2' : '1'}
              />
            </Fieldset>

            <SmartviewContentSeparator />

            <Fieldset cols={1}>
              <Fields.TextArea
                name='note'
                label={<Translation name='note.label' />}
              />
            </Fieldset>
          </Translator>
        </Smartview>
      </ModelWithForm>
    </Translator>
  )
}
